import React from 'react';
import { Table, TableColumn } from '@backstage/core-components';
import {
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Schedule as PendingIcon,
  Person as UserIcon,
  Business as ProductIcon,
  AccessTime as TimeIcon,
  Email as EmailIcon,
} from '@material-ui/icons';
import { formatTime, getProductLabel } from '../../../../utils';

const useStyles = makeStyles(theme => ({
  table: {
    '& th': {
      textTransform: 'none !important',
    },
  },
  pendingChip: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeaa7',
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  approvedChip: {
    backgroundColor: '#d1f2eb',
    color: '#0c5460',
    border: '1px solid #7dcea0',
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  rejectedChip: {
    backgroundColor: '#fadbd8',
    color: '#922b21',
    border: '1px solid #f1948a',
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  approveButton: {
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '8px',
    padding: '6px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#059669',
      transform: 'scale(1.05)',
    },
  },
  rejectButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    borderRadius: '8px',
    padding: '6px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#dc2626',
      transform: 'scale(1.05)',
    },
  },
}));

interface AccessRequest {
  id: number;
  product: string;
  requested_by: string;
  role: string;
  requested_at: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_by: string | null;
  approved_at: string | null;
}

interface ApprovalsTableProps {
  requests: AccessRequest[];
  canManageRequest: (request: AccessRequest) => boolean;
  onApprove: (request: AccessRequest) => void;
  onReject: (request: AccessRequest) => void;
}

export const ApprovalsTable: React.FC<ApprovalsTableProps> = ({
  requests,
  canManageRequest,
  onApprove,
  onReject,
}) => {
  const classes = useStyles();

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon />}
            label="Pending Review"
            className={classes.pendingChip}
            size="small"
            variant="outlined"
          />
        );
      case 'approved':
        return (
          <Chip
            icon={<ApproveIcon />}
            label="Approved"
            className={classes.approvedChip}
            size="small"
            variant="outlined"
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<RejectIcon />}
            label="Rejected"
            className={classes.rejectedChip}
            size="small"
            variant="outlined"
          />
        );
      default:
        return null;
    }
  };

  const columns: TableColumn[] = [
    {
      title: 'Requested By',
      field: 'requested_by',
      render: (rowData: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserIcon style={{ marginRight: 8, color: '#6b7280' }} />
          {rowData.requested_by}
        </div>
      ),
    },
    {
      title: 'Product',
      field: 'product',
      render: (rowData: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ProductIcon style={{ marginRight: 8, color: '#6b7280' }} />
          {getProductLabel(rowData.product)}
        </div>
      ),
      customFilterAndSearch: (term, rowData: any) =>
        getProductLabel(rowData.product).toLowerCase().includes(term.toLowerCase()),
    },
    {
      title: 'Requested Role',
      field: 'role',
      render: (rowData: any) => (
        <Chip
          label={rowData.role.toUpperCase()}
          size="small"
          style={{
            backgroundColor: '#e0e7ff',
            color: '#3730a3',
            fontWeight: 600,
          }}
        />
      ),
      customFilterAndSearch: (term, rowData: any) =>
        rowData.role.toUpperCase().includes(term.toUpperCase()),
    },
    {
      title: 'Requested At',
      field: 'requested_at',
      render: (rowData: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TimeIcon style={{ marginRight: 8, color: '#6b7280' }} />
          {formatTime(rowData.requested_at)}
        </div>
      ),
      customFilterAndSearch: (term, rowData: any) =>
        formatTime(rowData.requested_at).toLowerCase().includes(term.toLowerCase()),
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData: any) => getStatusChip(rowData.status),
    },
    {
      title: 'Approved By',
      field: 'approved_by',
      render: (rowData: any) =>
        rowData.approved_by ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon style={{ marginRight: 8, color: '#6b7280' }} />
            {rowData.approved_by}
          </div>
        ) : (
          <Typography variant="body2" style={{ color: '#9ca3af', fontStyle: 'italic' }}>
            -
          </Typography>
        ),
    },
    {
      title: 'Actions',
      field: 'actions',
      render: (rowData: any) => {
        if (rowData.status === 'pending' && canManageRequest(rowData)) {
          return (
            <div className={classes.actionButtons}>
              <Tooltip title="Approve Request">
                <IconButton
                  size="small"
                  className={classes.approveButton}
                  onClick={() => onApprove(rowData)}
                >
                  <ApproveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject Request">
                <IconButton
                  size="small"
                  className={classes.rejectButton}
                  onClick={() => onReject(rowData)}
                >
                  <RejectIcon />
                </IconButton>
              </Tooltip>
            </div>
          );
        }
        if (rowData.status === 'pending') {
          return (
            <Typography variant="body2" style={{ color: '#6b7280', fontStyle: 'italic' }}>
              No permission
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            style={{
              color: rowData.status === 'approved' ? '#059669' : '#dc2626',
              fontWeight: 500,
            }}
          >
            {rowData.status === 'approved' ? '✅ Approved' : '❌ Rejected'}
          </Typography>
        );
      },
      searchable: false,
    },
  ];

  return (
    <Box className={classes.table}>
      <Table
        options={{
          search: true,
          paging: true,
          pageSize: 10,
          padding: 'default',
          searchFieldAlignment: 'left',
        }}
        data={requests}
        columns={columns}
      />
    </Box>
  );
};
